import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export function CustomDropdown({
    value = null,
    onChange,

    options = [],

    optionValue = "value",
    optionLabel = "label",

    // Agrupa as opções sob cabeçalhos não selecionáveis — string (nome do campo) ou função (opt) => label do grupo.
    // As opções já devem chegar ordenadas por grupo; um cabeçalho é inserido a cada troca de grupo.
    groupBy,

    placeholder = "Selecione...",
    disabled = false,

    filter = false,
    filterPlaceholder = "Buscar...",

    label,
    hasError,
    id,
    htmlFor,
    required = false,
    labelChildren,

    emptyMessage = "Nenhuma opção encontrada",

    showClear = false,
}) {

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [position, setPosition] = useState(null);

    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    // =========================
    // Position (portal para document.body)
    // =========================
    // Necessário pois dropdowns dentro de um p-dialog do PrimeReact
    // ficam cortados: o .p-dialog tem overflow: hidden, então um menu
    // absolute nunca consegue "vazar" para fora do modal.

    const updatePosition = () => {

        const rect = triggerRef.current?.getBoundingClientRect();

        if (!rect) return;

        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < 300 && rect.top > spaceBelow;

        setPosition({
            top: rect.bottom,
            bottom: window.innerHeight - rect.top,
            left: rect.left,
            width: rect.width,
            openUpward,
        });
    };

    // =========================
    // Helpers
    // =========================

    const getOptionValue = (opt) => {

        if (typeof optionValue === "function") {
            return optionValue(opt);
        }

        return opt?.[optionValue];
    };

    const getOptionLabel = (opt) => {

        if (typeof optionLabel === "function") {
            return optionLabel(opt);
        }

        return opt?.[optionLabel];
    };

    // =========================
    // Selected option
    // =========================

    const selectedOption = useMemo(() => {

        return options.find(
            (o) => getOptionValue(o) === value
        );

    }, [value, options]);

    // =========================
    // Filter
    // =========================

    const filteredOptions = useMemo(() => {

        if (!filter || !search.trim()) {
            return options;
        }

        return options.filter((opt) =>
            getOptionLabel(opt)
                ?.toString()
                .toLowerCase()
                .includes(search.toLowerCase())
        );

    }, [options, search, filter]);

    // =========================
    // Group (cabeçalhos não selecionáveis intercalados com as opções, na ordem em que chegam)
    // =========================

    const getOptionGroup = (opt) =>
        typeof groupBy === "function" ? groupBy(opt) : opt?.[groupBy];

    const groupedItems = useMemo(() => {

        if (!groupBy) return null;

        const items = [];
        let lastGroup;

        filteredOptions.forEach((opt, index) => {
            const group = getOptionGroup(opt);
            if (index === 0 || group !== lastGroup) {
                items.push({ type: "header", key: `__group-${index}`, label: group });
                lastGroup = group;
            }
            items.push({ type: "option", opt });
        });

        return items;

    }, [filteredOptions, groupBy]);

    // =========================
    // Outside click
    // =========================

    useEffect(() => {

        const handleOutside = (e) => {

            if (
                containerRef.current?.contains(e.target) ||
                menuRef.current?.contains(e.target)
            ) {
                return;
            }

            setOpen(false);
            setSearch("");
        };

        document.addEventListener("mousedown", handleOutside);

        return () =>
            document.removeEventListener("mousedown", handleOutside);

    }, []);

    // Reposiciona o menu enquanto aberto (scroll do dialog, resize, etc.)
    useEffect(() => {

        if (!open) return;

        updatePosition();

        const handle = () => updatePosition();

        window.addEventListener("scroll", handle, true);
        window.addEventListener("resize", handle);

        return () => {
            window.removeEventListener("scroll", handle, true);
            window.removeEventListener("resize", handle);
        };

    }, [open]);

    // =========================
    // Select
    // =========================

    const selectOption = (opt) => {

        onChange({
            value: getOptionValue(opt)
        });

        setOpen(false);
        setSearch("");
    };

    const clear = (e) => {

        e.stopPropagation();

        onChange({
            value: null
        });
    };

    return (

        <div
            ref={containerRef}
            className="relative w-full"
        >

            {label &&

            <label
                htmlFor={htmlFor || id}
                className={`text-sm  mb-2.5 font-medium ${hasError ? "text-destructive" : "text-foreground/80"}`}>
                {label}
                {labelChildren}
                {required && <span className="text-destructive ml-1">*</span>}
            </label>
}


            {/* Trigger */}
            <div
                ref={triggerRef}
                onClick={() => {

                    if (disabled) return;

                    if (!open) {
                        updatePosition();
                    }

                    setOpen((o) => !o);
                }}
                className={`
                    h-9
                    w-full
                    flex
                    items-center
                    px-3
                    text-sm
                    border
                    rounded-md
                    cursor-pointer
                    select-none
                    transition-all
                    duration-150
                    outline-none

                    ${disabled
                        ? "bg-muted cursor-not-allowed"
                        : "bg-card"
                    }

                    ${hasError
                        ? `border-destructive ${open ? "ring-2 ring-destructive/20" : ""}`
                        : open
                            ? "border-primary ring-2 ring-primary/15"
                            : "border-input"
                    }
                `}
            >

                <span
                    className={`
                        text-sm
                        truncate

                        ${selectedOption
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }
                    `}
                >

                    {selectedOption
                        ? getOptionLabel(selectedOption)
                        : placeholder
                    }

                </span>

                <div className="ml-auto flex items-center gap-1 pl-2">

                    {showClear && value && !disabled && (

                        <button
                            onClick={clear}
                            className="
                                text-muted-foreground
                                hover:text-foreground
                                text-base
                                leading-none
                            "
                        >
                            ×
                        </button>
                    )}

                    <svg
                        className={`
                            w-4
                            h-4
                            text-muted-foreground
                            transition-transform
                            duration-200

                            ${open ? "rotate-180" : ""}
                        `}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>

                </div>

            </div>

            {/* Dropdown */}
            {open && position && createPortal(

                <div
                    ref={menuRef}
                    style={{
                        position: "fixed",
                        left: position.left,
                        width: position.width,

                        ...(position.openUpward
                            ? { bottom: position.bottom + 4 }
                            : { top: position.top + 4 }
                        ),
                    }}
                    className={`
                        z-[10000]
                        surface-panel
                        animate-in
                        fade-in
                        zoom-in-95
                        duration-150
                    `}
                >

                    {/* Filter */}
                    {filter && (

                        <div className="p-2 border-b border-border">

                            <div className="relative">

                                <svg
                                    className="
                                        absolute
                                        left-2.5
                                        top-1/2
                                        -translate-y-1/2
                                        w-3.5
                                        h-3.5
                                        text-muted-foreground
                                    "
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>

                                <input
                                    autoFocus
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(e.target.value)
                                    }
                                    placeholder={filterPlaceholder}
                                    className="
                                        w-full
                                        pl-8
                                        pr-3
                                        py-1.5
                                        text-sm
                                        border
                                        border-input
                                        rounded-md
                                        bg-card
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-2
                                        focus:ring-primary/15
                                    "
                                />

                            </div>

                        </div>
                    )}

                    {/* Options */}
                    <ul className="max-h-60 overflow-y-auto">

                        {filteredOptions.length === 0 ? (

                            <li
                                className="
                                    px-3
                                    py-4
                                    text-sm
                                    text-muted-foreground
                                    text-center
                                "
                            >
                                {emptyMessage}
                            </li>

                        ) : groupBy ? (

                            groupedItems.map((item) => {

                                if (item.type === "header") {
                                    return (
                                        <li
                                            key={item.key}
                                            className="
                                                px-3
                                                pt-2.5
                                                pb-1
                                                text-xs
                                                font-semibold
                                                uppercase
                                                tracking-wide
                                                text-muted-foreground
                                                select-none
                                                cursor-default
                                            "
                                        >
                                            {item.label}
                                        </li>
                                    );
                                }

                                const optValue =
                                    getOptionValue(item.opt);

                                const optLabel =
                                    getOptionLabel(item.opt);

                                const selected =
                                    value === optValue;

                                return (

                                    <li
                                        key={optValue}
                                        onClick={() =>
                                            selectOption(item.opt)
                                        }
                                        className={`
                                            px-3
                                            py-2
                                            text-sm
                                            cursor-pointer
                                            transition-colors
                                            mx-1
                                            rounded-md

                                            ${selected
                                                ? "bg-accent text-accent-foreground"
                                                : "text-foreground hover:bg-muted"
                                            }
                                        `}
                                    >

                                        {optLabel}

                                    </li>
                                );
                            })

                        ) : (

                            filteredOptions.map((opt) => {

                                const optValue =
                                    getOptionValue(opt);

                                const optLabel =
                                    getOptionLabel(opt);

                                const selected =
                                    value === optValue;

                                return (

                                    <li
                                        key={optValue}
                                        onClick={() =>
                                            selectOption(opt)
                                        }
                                        className={`
                                            px-3
                                            py-2
                                            text-sm
                                            cursor-pointer
                                            transition-colors
                                            mx-1
                                            rounded-md

                                            ${selected
                                                ? "bg-accent text-accent-foreground"
                                                : "text-foreground hover:bg-muted"
                                            }
                                        `}
                                    >

                                        {optLabel}

                                    </li>
                                );
                            })
                        )}

                    </ul>

                </div>,
                document.body
            )}

        </div>
    );
}